// @ts-nocheck

import { Close, Delete, Edit } from "@mui/icons-material";
import {
	AppBar,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Slide,
	styled,
	TextField,
	Toolbar,
	Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { api, types } from "api";
import clsx from "clsx";
import { AlertDialog } from "components/AlertDialog";
import { InfoSnackBar } from "components/InfoSnackBar";
import { StyledButton } from "components/StyledButton";
import { UserSearch } from "components/UserSearch";
import { useAuthStatus } from "helpers/useAuthStatus";
import { forwardRef, ReactElement, Ref, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { routes } from "routes";

const PREFIX = "ViewGroupsPageTableEditGroup";

const classes = {
	appBar: `${PREFIX}-appBar`,
	title: `${PREFIX}-title`,
	content: `${PREFIX}-content`,
	textfield: `${PREFIX}-textfield`,
	icon: `${PREFIX}-icon`,
	button: `${PREFIX}-button`,
	deleteGroupBtn: `${PREFIX}-deleteGroupBtn`,
};

const Root = styled(Box)(({ theme }) => ({
	[`& .${classes.appBar}`]: {
		position: "relative",
		backgroundColor: theme.palette.primary.main,
	},

	[`& .${classes.title}`]: {
		marginLeft: theme.spacing(2),
		flex: 1,
	},

	[`& .${classes.textfield}`]: {
		marginBottom: theme.spacing(4),
		backgroundColor: theme.palette.background.paper,
	},

	[`& .${classes.icon}`]: {
		paddingRight: theme.spacing(1),
	},

	[`& .${classes.button}`]: {
		whiteSpace: "nowrap",
	},

	[`& .${classes.deleteGroupBtn}`]: {
		marginRight: theme.spacing(1),
	},
}));

const Transition = forwardRef(function Transition(
	props: TransitionProps & { children: ReactElement },
	ref: Ref<unknown>,
) {
	return <Slide direction="up" ref={ref} {...props} />;
});

export interface ViewGroupsPageTableEditGroupProps {
	group: types.Group;
	handler: (group: types.Group) => void;
	// Used to delete groups from the parent list of groups
	deleteGroupFromList: (group: types.Group) => void;
}

export const ViewGroupsPageTableEditGroup = ({
	group,
	handler,
	deleteGroupFromList,
}: ViewGroupsPageTableEditGroupProps): JSX.Element => {
	const [open, setOpen] = useState(false);
	const [err, setErr] = useState(false);
	const [newGroup, setGroup] = useState(group);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isOwner, setIsOwner] = useState(false);
	const [alertOpen, setAlertOpen] = useState(false);
	const { role } = useAuthStatus();
	const history = useHistory();

	useEffect(() => {
		if (role === "admin") {
			setIsOwner(true);
			return;
		}
		api
			.getMyUserData()
			.then((res) => {
				if (res.status !== 200) {
					throw new Error("invalid error code");
				}
				if (res.data.userId in group.owners.map((owner) => owner.userId)) {
					setIsOwner(true);
				}
			})
			.catch(() => setErr(true));
	}, [group.owners, role]);

	const deleteGroup = async (): Promise<boolean> => {
		try {
			// Perform API call
			const res = await api.deleteGroup(group.groupId);
			const success = res.status === 201;
			if (success) {
				deleteGroupFromList(group);
			}
			setAlertOpen(res.status === 405);
			return success;
		} catch (error) {
			return false;
		}
	};

	const openDialog = (): void => {
		setIsDialogOpen(true);
	};

	const closeDialog = (): void => {
		setIsDialogOpen(false);
	};

	const handleClickOpen = (): void => {
		setOpen(true);
	};

	const handleClose = (): void => {
		setOpen(false);
	};

	const updateName = (): void => {
		api
			.updateGroup(newGroup.groupId, { name: newGroup.name, owners: [] })
			.then((res) => {
				if (res.status !== 201) {
					setErr(true);
				}
			})
			.catch(() => setErr(true));
	};

	const addUser = (ids: string[]): void => {
		api
			.addUsersToGroup(newGroup.groupId, ids)
			.then((res) => {
				if (res.status !== 201) {
					setErr(true);
				}
			})
			.catch(() => setErr(true));
	};

	const deleteUser = (ids: string[]): void => {
		api
			.deleteUsersFromGroup(newGroup.groupId, ids)
			.then((res) => {
				if (res.status !== 201) {
					setErr(true);
				}
			})
			.catch(() => setErr(true));
	};

	const submitChange = (): void => {
		if (group.name !== newGroup.name) {
			updateName();
		}
		// update students if new students have been added
		const existingIds: string[] = group.students.map((item) => item.userId);
		const newIds: string[] = newGroup.students.map((item2) => item2.userId);
		const deleteIds = existingIds.filter((value) => !newIds.includes(value));
		const addIds = newIds.filter((value) => !existingIds.includes(value));
		if (deleteIds.length > 0) {
			deleteUser(deleteIds);
		}
		if (addIds.length > 0) {
			addUser(addIds);
		}
		handleClose();
		handler(newGroup);
	};

	return (
		<Root display="flex" justifyContent="flex-end">
			{isOwner && (
				<>
					<StyledButton
						color="error"
						variant="contained"
						data-testid="delete-group-btn"
						onClick={openDialog}
						startIcon={<Delete />}
						className={clsx(classes.button, classes.deleteGroupBtn)}
					>
						DELETE GROUP
					</StyledButton>
					<StyledButton
						variant="contained"
						color="primary"
						onClick={handleClickOpen}
						data-testid="edit-btn"
						startIcon={<Edit />}
						className={classes.button}
					>
						Edit Group
					</StyledButton>
				</>
			)}
			<AlertDialog
				open={isDialogOpen}
				onClose={closeDialog}
				confirmAction={deleteGroup}
				title="Delete group confirmation"
				message={`Are you sure you want to delete ${group.name}?`}
				status={{
					success: "Successfully deleted group!",
					failure: "Failed to delete group.",
				}}
			/>

			<Dialog
				fullScreen
				open={open}
				onClose={handleClose}
				TransitionComponent={Transition}
			>
				<AppBar className={classes.appBar}>
					<Toolbar>
						<IconButton
							edge="start"
							color="inherit"
							onClick={handleClose}
							aria-label="close"
							size="large"
						>
							<Close />
						</IconButton>
						<Typography variant="h6" className={classes.title}>
							Edit Group
						</Typography>
						<Box flexGrow={1} />
						<StyledButton
							autoFocus
							color="inherit"
							onClick={submitChange}
							primaryBackground
						>
							Update
						</StyledButton>
					</Toolbar>
				</AppBar>
				<DialogContent className={classes.content} sx={{ paddingTop: 12 }}>
					<TextField
						label="Group Name"
						variant="outlined"
						color="primary"
						data-testid="group-name"
						value={newGroup.name}
						onChange={(e) => setGroup({ ...newGroup, name: e.target.value })}
						fullWidth
						className={classes.textfield}
					/>
					<UserSearch
						role="student"
						users={newGroup.students as types.User[]}
						setUsers={(users) => setGroup({ ...newGroup, students: users })}
					/>
					<InfoSnackBar
						isOpen={err}
						onClose={() => setErr(false)}
						message="Sorry something went wrong"
						severity="error"
					/>
				</DialogContent>
			</Dialog>
			<Dialog
				open={alertOpen}
				onClose={setAlertOpen}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">Alert</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						The group you tried to delete could not be deleted as you{" "}
						<strong> still have assignments for this group.</strong>
						<br />
						Please delete all the assignments if you want to delete the group.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setAlertOpen(false)} color="secondary">
						Cancel
					</Button>
					<Button
						onClick={() => history.push(routes._.assignments.path)}
						color="primary"
						autoFocus
					>
						Go to assignments
					</Button>
				</DialogActions>
			</Dialog>
		</Root>
	);
};
